// src/pages/Auditorias/hooks/useAuditoriaDetalhe.js
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  obterAuditoria, obterProblemas, obterFicheiros,
  validarExecucao, executarAuditoria, cancelarAuditoria, guardarNota
} from '../api';
import { estaEmCurso } from '../utils/formatacao';

/**
 * Detalhe de uma auditoria: mestre, problemas, árvore de ficheiros e histórico.
 *
 * Problemas e ficheiros são carregados a pedido (quando a secção abre) — numa
 * auditoria com 241 ficheiros não vale a pena trazer tudo à partida.
 */
export function useAuditoriaDetalhe(id) {
  const [auditoria, setAuditoria] = useState(null);
  const [problemas, setProblemas] = useState(null);
  const [ficheiros, setFicheiros] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const primeiraCarga = useRef(true);

  const carregar = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await obterAuditoria(id);
      setAuditoria(data.data);
      setErro(null);
    } catch (e) {
      console.error('Erro ao carregar auditoria:', e);
      setErro(e.response?.data?.message || 'Não foi possível carregar a auditoria');
    } finally {
      setCarregando(false);
      primeiraCarga.current = false;
    }
  }, [id]);

  useEffect(() => {
    if (primeiraCarga.current) setCarregando(true);
    carregar();
  }, [carregar]);

  const emCurso = auditoria && estaEmCurso(auditoria.estado);

  const carregarProblemas = useCallback(async (forcar = false) => {
    if (!id || (problemas && !forcar)) return;
    try {
      const { data } = await obterProblemas(id);
      setProblemas(data.data);
    } catch (e) {
      console.error('Erro ao carregar problemas:', e);
    }
  }, [id, problemas]);

  const carregarFicheiros = useCallback(async (forcar = false) => {
    if (!id || (ficheiros && !forcar)) return;
    try {
      const { data } = await obterFicheiros(id);
      setFicheiros(data.data);
    } catch (e) {
      console.error('Erro ao carregar ficheiros:', e);
    }
  }, [id, ficheiros]);

  /**
   * Actualização manual: recarrega o progresso e as secções já abertas.
   * Sem polling automático — ver nota em useAuditorias.js.
   */
  const actualizarTudo = useCallback(async () => {
    await carregar();
    if (problemas) await carregarProblemas(true);
    if (ficheiros) await carregarFicheiros(true);
  }, [carregar, problemas, ficheiros, carregarProblemas, carregarFicheiros]);

  const validar = useCallback(async (execId) => {
    await validarExecucao(id, execId);
    await carregar();
  }, [id, carregar]);

  const relancar = useCallback(async (modo) => {
    if (!auditoria) return null;
    const { data } = await executarAuditoria({
      cod_cli: auditoria.cod_cli,
      cod_processo: auditoria.cod_processo,
      user_email: auditoria.user_email,
      modo,
    });
    setProblemas(null);
    setFicheiros(null);
    await carregar();
    return data.data;
  }, [auditoria, carregar]);

  const cancelar = useCallback(async () => {
    const { data } = await cancelarAuditoria(id);
    await carregar();
    return data;
  }, [id, carregar]);

  const anotar = useCallback(async (ficheiroId, nota) => {
    await guardarNota(id, ficheiroId, nota);
    if (problemas) await carregarProblemas(true);
  }, [id, problemas, carregarProblemas]);

  return {
    auditoria,
    problemas,
    ficheiros,
    carregando,
    erro,
    emCurso,
    carregarProblemas,
    carregarFicheiros,
    validar,
    relancar,
    cancelar,
    anotar,
    actualizarTudo,
    recarregar: carregar,
  };
}
